
/**
 * Module dependencies.
 */

import { NavbarCard } from 'src/types/navbar';
import { color, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { isInternalRoute, navElementAttrs } from './nav-element';
import { useTranslate } from 'src/core/utils/translator';
import Image from 'src/components/core/image';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import arrowRightIcon from 'src/assets/svg/arrow-right-2.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  card: NavbarCard,
  className?: string
};

/**
 * `HoverImage` styled component.
 */

const HoverImage = styled(Image)`
  opacity: 0;
  transition: opacity ${theme('animations.defaultTransition')};
`;

/**
 * `BaseImage` styled component.
 */

const BaseImage = styled(Image)`
  transition: transform ${theme('animations.defaultTransition')};
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.a.attrs(navElementAttrs)<{ hasHoverImage: boolean }>`
  align-items: flex-end;
  display: flex;
  gap: ${units(2)};
  overflow: hidden;
  padding: ${units(4)};
  position: relative;
  text-decoration: none;

  &::after {
    background-image: linear-gradient(to bottom, ${color.transparentize('black', 0)}, ${color.transparentize('black', 0.8)});
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  &:focus-visible,
  &:hover {
    ${ifProp('hasHoverImage', css`
      ${HoverImage} {
        opacity: 1;
      }
    `, css`
      ${BaseImage} {
        transform: scale(1.04);
      }
    `)}
  }
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  flex: 1 0 0;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

/**
 * `Title` styled component.
 */

const Title = styled.p`
  color: ${color('white')};
  font-size: 24px;
  line-height: 1.5;
  margin: 0;
`;

/**
 * `Arrow` styled component.
 */

const Arrow = styled(Svg)`
  color: ${color('white')};
  flex: none;
  position: relative;
  z-index: 1;
`;

/**
 * `MenuCard` component.
 *
 * The back-office managed card of a mega menu. The hover image is optional —
 * without it the base image scales instead.
 */

const MenuCard = ({ card, className }: Props): ReactElement => {
  const { locale } = useTranslate();
  const { link, media, mediaHover, title } = card;

  return (
    <Wrapper
      className={className}
      hasHoverImage={!!mediaHover?.fileUrl}
      href={link}
      {...isInternalRoute(link) && { locale }}
    >
      <BaseImage
        alt={''}
        layout={'fill'}
        objectFit={'cover'}
        src={media.fileUrl}
      />

      {mediaHover?.fileUrl && (
        <HoverImage
          alt={''}
          layout={'fill'}
          objectFit={'cover'}
          src={mediaHover.fileUrl}
        />
      )}

      <Content>
        <Title>
          {title}
        </Title>
      </Content>

      <Arrow
        icon={arrowRightIcon}
        size={units(4)}
      />
    </Wrapper>
  );
};

/**
 * Export `MenuCard` component.
 */

export default MenuCard;
